import { toast } from 'sonner';
import { formatPrice } from './currency';

function fmtShort(iso) {
  if (!iso) return '';
  try { return new Date(iso + 'T00:00:00').toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }); }
  catch { return iso; }
}

// Core one-tap share: native share sheet when available, clipboard fallback otherwise.
async function nativeShare(title, text, url, copyToast) {
  const link = url || (typeof window !== 'undefined' ? window.location.href : '');
  if (typeof navigator !== 'undefined' && navigator.share) {
    try {
      await navigator.share({ title, text, url: link });
      return;
    } catch (e) {
      if (e && e.name === 'AbortError') return; // user cancelled — no error toast
    }
  }
  try {
    await navigator.clipboard.writeText(`${text}\n${link}`);
    toast.success(copyToast || 'Link copied — paste it to your travel companion');
  } catch {
    toast.error('Could not share');
  }
}

// Share a single flight fare. Link is the current results page URL (re-runs live search).
export async function shareFlight(offer, currency, url) {
  const route = `${offer.origin}\u2192${offer.destination}`;
  const price = formatPrice(offer.price_inr, currency);
  const when = offer.depart_date
    ? ` on ${fmtShort(offer.depart_date)}${offer.return_date ? `\u2013${fmtShort(offer.return_date)}` : ''}`
    : '';
  const airline = offer.airline_name ? ` with ${offer.airline_name}` : '';
  const text = `\u2708\ufe0f ${route} from ${price}${airline}${when} \u2014 found on FlightDeals.in`;
  await nativeShare(`${route} flight deal`, text, url, 'Deal link copied — paste it to your travel companion');
}

// Share a single weekend trip. Link deep-links to a live results search for that trip.
export async function shareWeekend(w, originCode, currency) {
  const params = new URLSearchParams({
    origin: originCode, destination: w.destination, trip_type: 'round_trip',
    depart_date: w.depart_date, return_date: w.return_date, adults: 1, children: 0, cabin: 'economy',
  }).toString();
  const base = typeof window !== 'undefined' ? window.location.origin : '';
  const link = `${base}/flights/results?${params}`;
  const where = w.destination_city || w.destination;
  const price = formatPrice(w.price_inr, currency);
  const text = `\u2708\ufe0f Weekend in ${where}: ${fmtShort(w.depart_date)}\u2013${fmtShort(w.return_date)} from ${price} round trip \u2014 found on FlightDeals.in`;
  await nativeShare(`Weekend in ${where}`, text, link, 'Weekend link copied — paste it to your travel companion');
}

// Share the whole Weekend Finder result set. Link re-opens the finder with the same inputs.
export async function shareWeekendSet({ originCode, originCity, destCode, destCity, month, monthLabel, longWeekend, anywhere, cheapestInr, currency }) {
  const qp = { origin: originCode, month };
  if (anywhere) qp.anywhere = '1'; else if (destCode) qp.destination = destCode;
  if (longWeekend) qp.long = '1';
  const base = typeof window !== 'undefined' ? window.location.origin : '';
  const link = `${base}/flights/weekends?${new URLSearchParams(qp).toString()}`;
  const where = anywhere ? 'to anywhere' : `to ${destCity || destCode}`;
  const kind = longWeekend ? 'long-weekend' : 'weekend';
  const from = cheapestInr ? `, from ${formatPrice(cheapestInr, currency)}` : '';
  const text = `Cheapest ${kind} escapes from ${originCity || originCode} ${where} in ${monthLabel}${from} \u2014 on FlightDeals.in`;
  await nativeShare('Weekend escapes', text, link, 'Weekend Finder link copied — share it with a travel buddy');
}
